import { useEffect, useRef } from 'react'

import { getFromSessionStorage } from '../utils/util'

import { INACTIVITY_TIMER } from '../constants/constant'

/**
 * Custom React hook that logs out the user and clears session/local storage after a period of inactivity.
 *
 * Sets up event listeners for user activity (click, mousemove, keydown, scroll, touchstart) and resets an inactivity timer on each event.
 * If the user is inactive for a specified duration (`INACTIVITY_TIMER`), the hook:
 *   - Alerts the user about logout due to inactivity.
 *   - Removes authentication token from session storage.
 *   - Clears various user-related data from local storage.
 *   - Redirects the user to the login page.
 *
 * @function useTabLocalInactivity
 * @returns {void}
 *
 * @requires getFromSessionStorage - Utility function to retrieve the token from session storage.
 * @requires INACTIVITY_TIMER - The duration (in milliseconds) after which the user is considered inactive.
 *
 * @example
 * useTabLocalInactivity();
 */

export const useTabLocalInactivity = () => {
  const timerRef = useRef(null)

  const token = getFromSessionStorage('token')

  useEffect(() => {
    if (!token) return
    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }

      timerRef.current = setTimeout(() => {
        alert('You have been logged out due to inactivity.')
        sessionStorage.removeItem('token')

        localStorage.removeItem('user')
        localStorage.removeItem('file')
        localStorage.removeItem('imgLink')
        localStorage.removeItem('educationData')
        localStorage.removeItem('email')
        localStorage.removeItem('professionalHeadlinesId')
        localStorage.removeItem('applicant-cv-data')
        localStorage.removeItem('applicant-data')
        localStorage.removeItem('reachPage')
        localStorage.removeItem('page')
        localStorage.removeItem('user_id')
        localStorage.removeItem('profileImgLink')
        localStorage.removeItem('cvImgLink')
        localStorage.removeItem('open_subscription')

        window.location.href = '/login'
      }, INACTIVITY_TIMER)
    }

    const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart']
    events.forEach((event) => window.addEventListener(event, resetTimer))

    resetTimer()

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer))
      clearTimeout(timerRef.current)
    }
  }, [])
}
